import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { GearSix, ChartPieSlice, PlayCircle, Sparkle } from '@phosphor-icons/react'

import { ToggleSwitch } from '@/components/ui/toggle-switch'

import API from '../../api/api'
import { selectUserData } from '../../reducers/userDataReducer'
import { NAVIGATION_LABELS } from '../../helpers/constants'
import NavigationLabel from '../NavigationLabel/NavigationLabel'
import VideoPreviewModal from '../VideoPreviewModal/VideoPreviewModal'

import QRCodeSurveysModal from './QRCodeSurveysModal'
import SurveySettings from './SurveySettingsModal'
import SurveyFormModal from './SurveyFormModal'

const SurveyHeader = ({
  analyticsOpen,
  buildings,
  onUIToggle,
  refreshTable,
  selectedBuilding,
  toggleAnalytics,
  userRole,
}) => {
  const { t } = useTranslation(['surveys'])

  const userData = useSelector(selectUserData)
  const userEmail = userData?.user?.email || ''
  const [suveyTemplatesStatus, setTemplatesStatus] = useState([])
  const [surveySettingsVisible, setSurveySettingsVisible] = useState(false)
  const [sendNewMessageFormVisible, setSendNewMessageFormVisible] = useState(false)
  const [isVideoModalVisible, setIsVideoModalVisible] = useState(false)
  const [isQRCodeSurveysModalVisible, setIsQRCodeSurveysModalVisible] = useState(false)

  useEffect(() => {
    if (selectedBuilding && selectedBuilding.id) {
      API.getSurveyTemplatesStatus(selectedBuilding.id)
        .then(({ data }) => setTemplatesStatus(data))
        .catch((err) => console.error(err))
    }
  }, [selectedBuilding])

  return (
    <div className="ctp-survey__header">
      <div className="ctp-survey__header-container">
        <div className="ctp-survey__header-title">{t('surveys:header.title')}</div>
        <button
          type="button"
          className="button button-blue"
          onClick={() => setIsVideoModalVisible(true)}
        >
          <PlayCircle size={16} />
          {t('surveys:header.watchTutorial')}
        </button>
        <button className="button button-transparent" onClick={toggleAnalytics}>
          <ChartPieSlice size={20} />
          {analyticsOpen ? t('surveys:header.hideAnalytics') : t('surveys:header.showAnalytics')}
        </button>
      </div>
      <div className="ctp-survey__header-container buttons-wrap">
        <ToggleSwitch
          falsyLabel={t('surveys:header.oldDesign')}
          truthyLabel={t('surveys:header.newDesign')}
          checked={false}
          onChange={onUIToggle}
        />
        <button
          type="submit"
          onClick={() => setSendNewMessageFormVisible(true)}
          className={`button button-black`}
        >
          {t('surveys:header.createNew')}
        </button>
        <button
          type="button"
          onClick={() => setIsQRCodeSurveysModalVisible(true)}
          className="button button-ai-gradient"
        >
          <Sparkle size={16} weight="fill" />
          {t('surveys:header.qrCodeSurveys')}
          <NavigationLabel type={NAVIGATION_LABELS.BETA} />
        </button>

        {!!suveyTemplatesStatus.length && (
          <>
            <div className="divider" />
            <button
              type="submit"
              onClick={() => setSurveySettingsVisible(true)}
              className={`button button-lightgray`}
            >
              <GearSix size={20} />
              {t('surveys:header.settings')}
            </button>
          </>
        )}
      </div>
      {sendNewMessageFormVisible && (
        <SurveyFormModal
          selectedBuilding={selectedBuilding}
          buildings={buildings}
          userRole={userRole}
          onClose={() => {
            setSendNewMessageFormVisible(false)
            refreshTable()
          }}
        />
      )}
      {surveySettingsVisible && (
        <SurveySettings
          buildingId={selectedBuilding && selectedBuilding.id}
          onClose={() => {
            setSurveySettingsVisible(false)
          }}
        />
      )}
      {isVideoModalVisible && (
        <VideoPreviewModal
          handleCloseVideo={() => setIsVideoModalVisible(false)}
          videoUrl="https://redvike-chefs.s3.us-east-1.amazonaws.com/Surveys.mp4"
        />
      )}
      {isQRCodeSurveysModalVisible && (
        <QRCodeSurveysModal
          onClose={() => setIsQRCodeSurveysModalVisible(false)}
          isExternal={selectedBuilding?.qrCodeSurveysAvailable}
          userEmail={userEmail}
        />
      )}
    </div>
  )
}

SurveyHeader.propTypes = {
  analyticsOpen: PropTypes.bool,
  buildings: PropTypes.array,
  mailingListOptions: PropTypes.array,
  onUIToggle: PropTypes.func,
  refreshTable: PropTypes.func,
  selectedBuilding: PropTypes.object,
  toggleAnalytics: PropTypes.func,
  userRole: PropTypes.string,
}

export default SurveyHeader
